import { ElementRef, useRef } from 'react';
import { useMemo } from 'react';

import { Box, Grid, Heading, Hide, useMediaQuery, useQuery } from '@chakra-ui/react';

import { Slider } from '@/components/functional';
import { SectionContentWrapper, SectionWrapper } from '@/components/wrappers';
import { DynamicChartSectionFragment } from '@/graphql/generated';

import { SliderControls } from '../../../functional/SliderControls/SliderControls';
import { ChartPoint } from './components/ChartPoint';

export const DynamicChartSection = ({ header, items }: DynamicChartSectionFragment) => {
  const [isAboveXS] = useMediaQuery(useQuery({ above: 'xs' }));
  const sliderRef = useRef<ElementRef<typeof Slider>>(null);
  const words = header.split(' ');
  const chunks = useMemo(
    () => Array.from({ length: Math.ceil(words.length / 3) }).map((_, i) => words.slice(i * 3, i * 3 + 3).join(' ')),
    [words],
  );

  return (
    <SectionWrapper backgroundColor="surface.500">
      <Box zIndex="0" overflow="hidden" maxWidth="100%" paddingTop="70px!" paddingBottom="70px!" position="relative">
        <SectionContentWrapper zIndex="0" overflow="hidden" paddingTop="80px!" paddingBottom="80px!">
          <Grid
            gridTemplateColumns={{
              base: '[column1-start column-start] minmax(0, 100%) [column1-end column2-start] 0 [column2-end column-end]',
            }}
            gridTemplateRows={{
              base: '[row1-start row-start] min-content [row1-end row2-start] min-content [row2-end row3-start] auto [row3-end row4-start] auto [row4-end row5-start] auto [row5-end row-end]',
            }}
            columnGap={{}}
          >
            <Heading
              as="h2"
              fontFamily={'body'}
              fontWeight={400}
              color="white"
              fontSize={{ base: '36px', lg: '60px' }}
              marginBottom={{ base: '6px', sm: '6px' }}
              maxWidth={{ base: '300px', xs: '450px', sm: '650px', xl: '800px' }}
              minHeight={{ xl: '187px' }}
              gridColumnStart={{ base: 'column1-start' }}
              gridColumnEnd={{ base: 'column1-end' }}
              gridRowStart={{ base: 'row3-start', sm: 'row2-start' }}
              gridRowEnd={{ base: 'row3-end', sm: 'row2-start' }}
            >
              {chunks.map((line, index) => (
                <Box as="span" key={index} display="block">
                  {line}
                </Box>
              ))}
            </Heading>
            <Hide above="2xl">
              <SliderControls
                pt={{ base: 8 }}
                onSlidePrev={() => sliderRef.current?.onPrev()}
                onSlideNext={() => sliderRef.current?.onNext()}
                /* GRID ITEM 3 */
                gridColumnStart={{ base: 'column1-start' }}
                gridColumnEnd={{ base: 'column1-end' }}
                gridRowStart={{ base: 'row4-start', sm: 'row3-start' }}
                gridRowEnd={{ base: 'row4-end', sm: 'row3-start' }}
                alignSelf="center"
                justifySelf={{ base: 'self-start' }}
                variant="light"
              />
            </Hide>
            <Grid
              mx={{ base: -5, xs: 0 }}
              pt={{ base: 8, sm: 12 }}
              /* GRID ITEM 4 */
              gridColumnStart={{ base: 'column1-start' }}
              gridColumnEnd={{ base: 'column1-end' }}
              gridRowStart={{ base: 'row5-start', sm: 'row4-start' }}
              gridRowEnd={{ base: 'row5-end', sm: 'row4-end' }}
            >
              <Slider ref={sliderRef} snapDirection={isAboveXS ? 'start' : 'center'} pt="5px" gapPX={1}>
                {items.map((el, index) => {
                  if (el && el.heading && el.list) {
                    return <ChartPoint key={index} title={el?.heading || ''} points={el?.list || []} index={index} />;
                  }
                  return null;
                })}
              </Slider>
            </Grid>
          </Grid>
        </SectionContentWrapper>
      </Box>
    </SectionWrapper>
  );
};
